import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompaniesContext } from '../../../contexts/companies';
import useRouter from '../../../hooks/router/useRouter';
import { getReadEnrolmentsQueryKey, useReadEnrolments, useUpdateEnrolment, useUpdateAdminEnrolmentEmail, } from '../../../services/online/enrolments/enrolments';
import { getReadMeQueryKey, useReadPerson, } from '../../../services/online/people/people';
import { Role } from '../../../services/types';
import { tKeys } from '../../../translations/keys';
import { createForm } from '../../../utils/formik';
import { useDisclosure } from '@chakra-ui/react';
import { useToast } from '../../../components/content/use-toast/index.web';
const useLogic = () => {
    // Attributes
    const { goBack, params } = useRouter();
    const { selectedCompanyId, selectedEnrolment } = useCompaniesContext();
    const personId = params?.personId;
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const isAdmin = selectedEnrolment?.role === Role?.[0];
    const { isOpen: isOpenMemberRole, onToggle: onToggleMemberRole } = useDisclosure();
    const { isOpen: isOpenBoardRole, onToggle: onToggleBoardRole } = useDisclosure();
    const toast = useToast();
    const { mutateAsync: updateAdminEnrolmentEmail, isLoading: isUpdateAdminEnrolmentEmailLoading, } = useUpdateAdminEnrolmentEmail();
    const { isOpen: isEmailModalOpen, onOpen: onEmailModalOpen, onClose: onEmailModalClose, } = useDisclosure();
    // Queries
    const { data: personData, refetch: refetchPersonData } = useReadPerson(personId, {
        'company-id': selectedCompanyId,
    }, {
        query: {
            enabled: !!personId && !!selectedCompanyId,
        },
    });
    const { data: companyEnrolements } = useReadEnrolments({
        'company-id': selectedCompanyId,
        'page-size': 1000,
        'show-intactive': true,
    }, {
        query: {
            enabled: !!selectedCompanyId,
        },
    });
    const { mutateAsync: updateEnrolment, isLoading: isUpdateEnrolmentLoading } = useUpdateEnrolment();
    // Functions
    const personEnrolement = useMemo(() => {
        return companyEnrolements?.items?.find(companyEnrolement => companyEnrolement?.personId === personId);
    }, [companyEnrolements, personId]);
    // Functions
    async function handleOnSubmit() {
        if (!dirty)
            return;
        try {
            await updateEnrolment({
                data: {
                    ...personEnrolement,
                    role: form?.memberRole?.value,
                    endedOn: form?.govrnUserUntil?.value,
                    boardRoleId: form?.boardRoleId?.value,
                },
            }, {
                onSuccess: () => {
                    void queryClient.invalidateQueries(getReadEnrolmentsQueryKey({
                        'company-id': selectedCompanyId,
                    }));
                    // Refetch data
                    void queryClient.invalidateQueries(getReadMeQueryKey());
                    void queryClient.invalidateQueries(getReadEnrolmentsQueryKey());
                    toast({
                        status: 'success',
                        title: t(tKeys.other_member.update.successfull),
                    });
                    goBack();
                },
                onError: () => {
                    toast({
                        status: 'error',
                        title: t(tKeys.other_member.update.error),
                    });
                },
            });
        }
        catch (err) {
            console.error('Error when updating the enrolement : ', err);
        }
    }
    function handleSuspendUser() {
        form?.govrnUserUntil?.onChange(moment().toISOString());
    }
    function handleOnChooseRole(role) {
        form?.memberRole?.onChange?.(role);
        onToggleMemberRole();
    }
    function handleChooseBoardRole(boardRole) {
        form?.boardRoleId?.onChange?.(boardRole.id);
        form?.boardRoleName?.onChange?.(boardRole.name);
        onToggleBoardRole();
    }
    // Form
    const { initialValues, values, dirty, ...rest } = useFormik({
        initialValues: {
            memberRole: personEnrolement?.role,
            boardRoleId: personEnrolement?.boardRoleId,
            boardRoleName: personEnrolement?.boardRoleName,
            govrnUserUntil: personEnrolement?.endedOn,
        },
        onSubmit: handleOnSubmit,
        enableReinitialize: true,
    });
    const form = createForm(values, rest);
    const isMemberSuspended = useMemo(() => {
        return moment(form?.govrnUserUntil?.value || personEnrolement?.endedOn).isSameOrBefore(moment());
    }, [form?.govrnUserUntil?.value, personEnrolement?.endedOn]);
    // Effects
    useEffect(() => {
        if (!personData)
            return;
        setTimeout(() => (document.title = `${selectedEnrolment.company?.name} | ${t(personData?.firstname)} ${t(personData?.lastname)}`), 200);
    }, [personData, selectedEnrolment]);
    // Formulaire pour la mise à jour de l'email
    const [emailForm, setEmailForm] = useState({
        email: {
            value: '',
            error: '',
            onChange: (text) => {
                setEmailForm(prev => ({
                    ...prev,
                    email: { ...prev.email, value: text, error: '' },
                    isValid: validateEmailForm(text, prev.confirmEmail.value),
                }));
            },
        },
        confirmEmail: {
            value: '',
            error: '',
            onChange: (text) => {
                setEmailForm(prev => ({
                    ...prev,
                    confirmEmail: { ...prev.confirmEmail, value: text, error: '' },
                    isValid: validateEmailForm(prev.email.value, text),
                }));
            },
        },
        isLoading: false,
        isValid: false,
    });
    // Formulaire pour la mise à jour du téléphone
    const [phoneForm, setPhoneForm] = useState({
        phone: {
            value: '',
            countryCode: '',
            error: '',
            onChange: (text, countryCode) => {
                setPhoneForm(prev => ({
                    ...prev,
                    phone: { ...prev.phone, value: text, countryCode, error: '' },
                    isValid: validatePhoneForm(text, countryCode, prev.confirmPhone.value, prev.confirmPhone.countryCode),
                }));
            },
        },
        confirmPhone: {
            value: '',
            countryCode: '',
            error: '',
            onChange: (text, countryCode) => {
                setPhoneForm(prev => ({
                    ...prev,
                    confirmPhone: {
                        ...prev.confirmPhone,
                        value: text,
                        countryCode,
                        error: '',
                    },
                    isValid: validatePhoneForm(prev.phone.value, prev.phone.countryCode, text, countryCode),
                }));
            },
        },
        isLoading: false,
        isValid: false,
    });
    // Valider le formulaire d'email
    const validateEmailForm = (email, confirmEmail) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email) && email === confirmEmail;
    };
    // Valider le formulaire de téléphone
    const validatePhoneForm = (phone, phoneCountryCode, confirmPhone, confirmPhoneCountryCode) => {
        return (phone &&
            phoneCountryCode &&
            phone === confirmPhone &&
            phoneCountryCode === confirmPhoneCountryCode);
    };
    // Mettre à jour l'email de l'utilisateur
    const handleUpdateEmail = async () => {
        if (!emailForm.isValid)
            return;
        try {
            setEmailForm(prev => ({ ...prev, isLoading: true }));
            // Mettre à jour l'appel API
            updateAdminEnrolmentEmail({
                enrolmentId: personEnrolement?.id,
                data: {
                    email: emailForm.email.value,
                },
            }, {
                onSuccess: async () => {
                    // Invalidate all relevant queries
                    await Promise.all([
                        queryClient.invalidateQueries(['person', personId]),
                        queryClient.invalidateQueries(getReadMeQueryKey()),
                        queryClient.invalidateQueries(getReadEnrolmentsQueryKey({
                            'company-id': selectedCompanyId,
                        })),
                    ]);
                    // Force refetch of person data
                    await refetchPersonData();
                    // Reset form and close modal
                    setEmailForm(prev => ({
                        ...prev,
                        email: { ...prev.email, value: '' },
                        confirmEmail: { ...prev.confirmEmail, value: '' },
                        isLoading: false,
                        isValid: false,
                    }));
                    onEmailModalClose();
                    toast({
                        status: 'success',
                        title: t(tKeys.common.email_updated_success),
                    });
                },
                onError: () => {
                    toast({
                        status: 'error',
                        title: t(tKeys.common.error),
                    });
                    setEmailForm(prev => ({ ...prev, isLoading: false }));
                },
            });
        }
        catch (error) {
            console.error('Error updating email:', error);
            toast({
                status: 'error',
                title: t(tKeys.common.error),
            });
            setEmailForm(prev => ({ ...prev, isLoading: false }));
        }
    };
    // Mise à jour de l'API pour le téléphone
    const handleUpdatePhone = async () => {
        if (!phoneForm.isValid)
            return;
        try {
            setPhoneForm(prev => ({ ...prev, isLoading: true }));
            // Format phone number with country code
            const formattedPhone = `${phoneForm.phone.countryCode}${phoneForm.phone.value}`;
            // Mettre à jour l'appel API
            await queryClient.fetchQuery(['updatePhone', personId], async () => await fetch(`/api/people/${personId}/phone`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Company-Id': selectedCompanyId,
                },
                body: JSON.stringify({ phone: formattedPhone }),
            }).then(async (res) => {
                if (!res.ok)
                    throw new Error('Failed to update phone');
                return await res.json();
            }));
            // Refresh user data
            await queryClient.invalidateQueries(['person', personId]);
            // Reset form and close modal
            setPhoneForm(prev => ({
                ...prev,
                phone: { ...prev.phone, value: '', countryCode: '' },
                confirmPhone: { ...prev.confirmPhone, value: '', countryCode: '' },
                isLoading: false,
                isValid: false,
            }));
            toast({
                status: 'success',
                title: t(tKeys.other_profile.phone_updated_success),
            });
        }
        catch (error) {
            console.error('Error updating phone:', error);
            toast({
                status: 'error',
                title: t(tKeys.common.error),
            });
            setPhoneForm(prev => ({ ...prev, isLoading: false }));
        }
    };
    // Return
    return {
        personData,
        form,
        personEnrolement,
        isAdmin,
        handleOnSubmit,
        isUpdateEnrolmentLoading,
        handleSuspendUser,
        isMemberSuspended,
        isOpenMemberRole,
        onToggleMemberRole,
        handleOnChooseRole,
        goBack,
        handleChooseBoardRole,
        isOpenBoardRole,
        onToggleBoardRole,
        emailForm,
        phoneForm,
        handleUpdateEmail,
        handleUpdatePhone,
        isEmailModalOpen,
        onEmailModalOpen,
        onEmailModalClose,
    };
};
export default useLogic;
